import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Database,
  Plus,
  RefreshCw,
  Folder,
  Trash2,
  X,
  Info,
  AlertTriangle,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileCode,
  FileVideo,
  FileAudio,
  FileArchive,
  FileJson,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { DataSource, Document } from '../types';
import { formatDate } from '../utils/dateUtils';
import { truncateDisplayName } from '../utils/textUtils';
import ConnectorLogo, { hasConnectorLogo } from '../components/ConnectorLogo';
import SyncProgressBar from '../components/SyncProgressBar';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';

/** Get icon and styles for a document based on filename extension (same as Knowledge Base / Global Documents) */
function getFileTypeIcon(filename: string): {
  Icon: React.ComponentType<{ className?: string }>;
  bgClass: string;
  iconClass: string;
} {
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  const text = ['pdf', 'doc', 'docx', 'rtf', 'odt', 'txt', 'md', 'odf'];
  const spreadsheet = ['xls', 'xlsx', 'ods', 'csv'];
  const image = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  const code = ['py', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 'xml', 'yaml', 'yml', 'sh', 'sql'];
  const video = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
  const audio = ['mp3', 'wav', 'ogg', 'm4a', 'flac'];
  const archive = ['zip', 'tar', 'gz', 'rar', '7z'];
  if (ext === 'pdf') return { Icon: FileText, bgClass: 'bg-red-100', iconClass: 'text-red-600' };
  if (ext === 'json') return { Icon: FileJson, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' };
  if (text.includes(ext)) return { Icon: FileText, bgClass: 'bg-blue-100', iconClass: 'text-blue-600' };
  if (spreadsheet.includes(ext)) return { Icon: FileSpreadsheet, bgClass: 'bg-green-100', iconClass: 'text-green-600' };
  if (image.includes(ext)) return { Icon: FileImage, bgClass: 'bg-purple-100', iconClass: 'text-purple-600' };
  if (code.includes(ext)) return { Icon: FileCode, bgClass: 'bg-gray-100', iconClass: 'text-gray-600' };
  if (video.includes(ext)) return { Icon: FileVideo, bgClass: 'bg-pink-100', iconClass: 'text-pink-600' };
  if (audio.includes(ext)) return { Icon: FileAudio, bgClass: 'bg-indigo-100', iconClass: 'text-indigo-600' };
  if (archive.includes(ext)) return { Icon: FileArchive, bgClass: 'bg-yellow-100', iconClass: 'text-yellow-600' };
  return { Icon: FileText, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' };
}

const formatDateLocal = (dateString: string) =>
  formatDate(dateString, { year: 'numeric', month: 'short', day: 'numeric' });

const CONNECTOR_LABELS: Record<string, string> = {
  google_drive: 'Google Drive',
  google_search_console: 'Google Search Console',
};

function getConnectorLabel(connectorType: string): string {
  if (CONNECTOR_LABELS[connectorType]) return CONNECTOR_LABELS[connectorType];
  return (connectorType || 'other').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const DataSources: React.FC = () => {
  const navigate = useNavigate();
  const { id: selectedId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set());
  const [dataSourceToDelete, setDataSourceToDelete] = useState<DataSource | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteDataSourceConfirmed, setDeleteDataSourceConfirmed] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedDs, setSelectedDs] = useState<DataSource | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSyncing, setDetailSyncing] = useState(false);
  const [detailPolling, setDetailPolling] = useState(false);

  const fetchDataSources = useCallback(async () => {
    try {
      const res = await apiService.getDataSources();
      setDataSources(res.data_sources || []);
      return res.data_sources || [];
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load data sources');
      return [];
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const credentialId = params.get('credential_id');
    if (status === 'connected' && credentialId) {
      const connectorType = params.get('connector_type');
      toast.success(
        connectorType === 'google_search_console'
          ? 'Google Search Console connected! Select a property.'
          : 'Connected! Select a folder to sync.'
      );
      const q = new URLSearchParams();
      q.set('credential_id', credentialId);
      if (connectorType) q.set('connector_type', connectorType);
      const parentFolderId = params.get('parent_folder_id');
      if (parentFolderId) q.set('parent_folder_id', parentFolderId);
      navigate(`/data-sources/add?${q.toString()}`, { replace: true });
    } else if (status === 'error') {
      const message = params.get('message') || 'Connection failed';
      // "Scope has changed" = app now requests new scopes (e.g. Search Console); user must re-authorize
      const isScopeChanged = /scope has changed/i.test(message);
      if (isScopeChanged) {
        toast.error(
          'Google needs you to sign in again after new permissions were added. Revoke this app in your Google account (Security → Third-party access), then try connecting again.'
        );
        console.error('[OAuth / Scope]', {
          message,
          hint: 'User must revoke app access and re-authorize so the new scope is granted. See docs/GOOGLE_CLOUD_SCOPES_SETUP.md',
        });
      } else {
        toast.error(message);
        const isScopeRelated =
          /scope|invalid_scope|access_denied|access blocked|invalid request/i.test(message);
        if (isScopeRelated) {
          console.error('[OAuth / Scope]', {
            message,
            hint: 'Add the required scope in Google Cloud Console → OAuth consent screen → Scopes. See docs/GOOGLE_CLOUD_SCOPES_SETUP.md',
          });
        } else {
          console.error('[OAuth] Connection error:', message);
        }
      }
    }
  }, [navigate]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchDataSources();
      setLoading(false);
    };
    load();
  }, [fetchDataSources]);

  const dataSourcesByConnector = useMemo(() => {
    const map: Record<string, DataSource[]> = {};
    dataSources.forEach((ds) => {
      const key = ds.connector_type || 'other';
      if (!map[key]) map[key] = [];
      map[key].push(ds);
    });
    return map;
  }, [dataSources]);

  const connectorTypesSorted = useMemo(() => {
    const keys = Object.keys(dataSourcesByConnector);
    return keys.sort((a, b) =>
      getConnectorLabel(a).localeCompare(getConnectorLabel(b), undefined, { sensitivity: 'base' })
    );
  }, [dataSourcesByConnector]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedDs(null);
      setSelectedDocuments([]);
      return;
    }
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const [dsRes, docsRes] = await Promise.all([
          apiService.getDataSource(selectedId),
          apiService.getDocuments(undefined, undefined, { dataSourceId: selectedId }),
        ]);
        setSelectedDs(dsRes);
        setSelectedDocuments(docsRes || []);
        if (dsRes?.last_sync_status === 'syncing') setDetailPolling(true);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load');
        setSelectedDs(null);
        setSelectedDocuments([]);
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetail();
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || !detailPolling) return;
    const iv = setInterval(async () => {
      try {
        const status = await apiService.getDataSourceSyncStatus(selectedId);
        if (status.status === 'syncing' && status.sync_progress) {
          setSelectedDs((prev) => (prev ? { ...prev, sync_progress: status.sync_progress } : null));
        }
        if (status.status !== 'syncing') {
          setDetailPolling(false);
          const [dsRes, docsRes] = await Promise.all([
            apiService.getDataSource(selectedId),
            apiService.getDocuments(undefined, undefined, { dataSourceId: selectedId }),
          ]);
          setSelectedDs(dsRes);
          setSelectedDocuments(docsRes || []);
        }
      } catch {
        setDetailPolling(false);
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [selectedId, detailPolling]);

  useEffect(() => {
    if (syncingIds.size === 0) return;
    const iv = setInterval(async () => {
      const list = await fetchDataSources();
      setSyncingIds((prev) => {
        const next = new Set(prev);
        Array.from(prev).forEach((sid) => {
          const ds = list.find((d) => d.id === sid);
          if (ds && ds.last_sync_status !== 'syncing') next.delete(sid);
        });
        return next;
      });
    }, 2500);
    return () => clearInterval(iv);
  }, [syncingIds, fetchDataSources]);

  const handleSync = async (id: string) => {
    setSyncingIds(prev => new Set(prev).add(id));
    try {
      await apiService.triggerDataSourceSync(id);
      toast.success('Sync started');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed');
      setSyncingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const handleDeleteClick = (ds: DataSource) => {
    setDataSourceToDelete(ds);
    setDeleteDataSourceConfirmed(false);
  };

  const handleDeleteConfirm = async () => {
    if (!dataSourceToDelete) return;
    const idToDelete = dataSourceToDelete.id;
    setDeletingId(idToDelete);
    try {
      await apiService.deleteDataSource(idToDelete);
      toast.success('Data source deleted');
      setDataSourceToDelete(null);
      if (selectedId === idToDelete) {
        setSelectedDs(null);
        setSelectedDocuments([]);
        navigate('/data-sources');
      }
      await fetchDataSources();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete data source');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setDataSourceToDelete(null);
    setDeleteDataSourceConfirmed(false);
  };

  const driveFolderPath = (ds: DataSource) => {
    const name = ds.config?.drive_folder_name;
    return name || 'Google Drive folder';
  };

  const statusLabel = (ds: DataSource) => {
    const s = ds.last_sync_status;
    if (s === 'syncing') return null;
    if (s === 'success') return ds.last_sync_at ? `Synced ${formatDate(ds.last_sync_at)}` : 'Synced';
    if (s === 'failed') return 'Failed';
    return 'Never synced';
  };

  const handleDetailSync = async () => {
    if (!selectedId) return;
    setDetailSyncing(true);
    setSyncingIds((prev) => new Set(prev).add(selectedId));
    try {
      await apiService.triggerDataSourceSync(selectedId);
      setDetailPolling(true);
      toast.success('Sync started');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setDetailSyncing(false);
    }
  };

  const handleAsk = () => {
    if (selectedDs?.folder_id) navigate(`/folder/${selectedDs.folder_id}`);
  };

  const selectedConnectorType = searchParams.get('connector');
  const listToShow = useMemo(
    () =>
      selectedConnectorType
        ? (dataSourcesByConnector[selectedConnectorType] || [])
        : dataSources,
    [selectedConnectorType, dataSourcesByConnector, dataSources]
  );
  const listLabel = selectedConnectorType ? getConnectorLabel(selectedConnectorType) : 'All';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full min-w-0 overflow-hidden">
      <div className="border-b border-gray-200 px-6 py-4 bg-white flex-shrink-0">
        <div className="flex items-center">
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Database className="h-6 w-6 text-primary-600" />
            Data Sources
            <button
              type="button"
              onClick={() => setShowInfoModal(true)}
              className="ml-2 p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
              title="Learn about Data Sources"
            >
              <Info className="h-5 w-5" />
            </button>
          </h1>
        </div>
        <p className="text-gray-600 mt-2">
          {dataSources.length === 0
            ? 'Connect Google Drive, Google Search Console, or other sources to sync documents into your workspace.'
            : `${dataSources.length} data source${dataSources.length !== 1 ? 's' : ''} connected. Sync and manage them below.`}
        </p>
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0 min-w-0">
        <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto px-6 py-6 bg-gray-50">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : dataSources.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">No data sources</h2>
              <p className="text-gray-500 mb-6">Connect Google Drive, Google Search Console, or other sources to sync documents into your workspace.</p>
              <Link
                to="/data-sources/add"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Add Data Source
              </Link>
            </div>
          ) : !selectedId ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                {listLabel === 'All'
                  ? `All ${dataSources.length} data source${dataSources.length !== 1 ? 's' : ''}. Click a type in the sidebar to filter, or a connection to view details.`
                  : `${listLabel}: ${listToShow.length} connection${listToShow.length !== 1 ? 's' : ''}. Click one to view its details and documents.`}
              </p>
              {listToShow.map((ds) => (
                <div
                  key={ds.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      {ds.connector_type && hasConnectorLogo(ds.connector_type) ? (
                        <ConnectorLogo connectorType={ds.connector_type} size={28} alt={ds.connector_type} />
                      ) : (
                        <Folder className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-gray-900 truncate">{ds.display_name}</h3>
                      <p className="text-sm text-gray-500 truncate">{driveFolderPath(ds)}</p>
                      <div className="mt-1">
                        {ds.last_sync_status === 'syncing' ? (
                          <SyncProgressBar progress={ds.sync_progress} compact />
                        ) : (
                          <p className="text-xs text-gray-400">{statusLabel(ds)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleSync(ds.id)}
                      disabled={syncingIds.has(ds.id) || ds.last_sync_status === 'syncing'}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncingIds.has(ds.id) || ds.last_sync_status === 'syncing' ? 'animate-spin' : ''}`} />
                      Sync Now
                    </button>
                    <Link
                      to={`/data-sources/${ds.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      View
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(ds)}
                      disabled={deletingId === ds.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      title="Delete data source"
                    >
                      <Trash2 className="w-4 h-4" />
                      {deletingId === ds.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : detailLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : selectedDs ? (
            <>
              <div className="border-b border-gray-200 pb-4 mb-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {selectedDs.connector_type && hasConnectorLogo(selectedDs.connector_type) && (
                      <div className="flex-shrink-0">
                        <ConnectorLogo connectorType={selectedDs.connector_type} size={40} alt="" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h2 className="text-xl font-semibold text-gray-900 truncate">{selectedDs.display_name}</h2>
                      <p className="text-sm text-gray-500 mt-0.5">{driveFolderPath(selectedDs)}</p>
                      <div className="mt-2">
                        {selectedDs.last_sync_status === 'syncing' ? (
                          <SyncProgressBar progress={selectedDs.sync_progress} />
                        ) : (
                          <p className="text-sm text-gray-400">
                            {selectedDs.last_sync_at ? `Last synced: ${formatDate(selectedDs.last_sync_at)}` : 'Never synced'}
                          </p>
                        )}
                      </div>
                      {selectedDs.last_sync_error && (
                        <p className="text-sm text-red-600 mt-1">{selectedDs.last_sync_error}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={handleDetailSync}
                      disabled={detailSyncing || selectedDs.last_sync_status === 'syncing'}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
                    >
                      <RefreshCw className={`w-4 h-4 ${detailSyncing || selectedDs.last_sync_status === 'syncing' ? 'animate-spin' : ''}`} />
                      Sync Now
                    </button>
                    <button
                      onClick={handleAsk}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Ask about this source
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(selectedDs)}
                      disabled={!!deletingId}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      title="Delete data source"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden bg-white">
                <h3 className="text-sm font-semibold text-gray-900 px-3 py-2.5 border-b border-gray-200 bg-gray-50">Documents ({selectedDocuments.length})</h3>
                {selectedDocuments.length === 0 ? (
                  <p className="text-gray-500 px-3 py-4 text-sm">No documents synced yet.</p>
                ) : (
                  <>
                    {selectedDocuments.map((doc) => {
                      const status = doc.status || doc.processing_status || '';
                      const isReady = status === 'ready' || status === 'completed';
                      const isProcessing = ['processing', 'queued', 'pending', 'indexing', 'uploaded'].includes(status);
                      const isFailed = status === 'failed' || status === 'error';
                      const { Icon, bgClass, iconClass } = getFileTypeIcon(doc.filename || doc.title || '');
                      return (
                        <Link
                          key={doc.doc_id}
                          to={`/document/${doc.doc_id}`}
                          className="py-2.5 px-3 flex items-center gap-3 hover:bg-gray-50 transition-colors block"
                        >
                          <div className={`w-10 h-10 flex-shrink-0 ${bgClass} rounded-lg flex items-center justify-center`}>
                            <Icon className={`h-5 w-5 ${iconClass}`} />
                          </div>
                          <div className="flex-1 min-w-0 max-w-[100ch]">
                            <p className="text-sm font-medium text-gray-900 truncate" title={doc.title}>
                              {truncateDisplayName(doc.title)}
                              <span className="ml-2 text-gray-500 font-normal">· {doc.chunks ?? 0} chunks</span>
                              {doc.created_at && (
                                <span className="ml-2 text-gray-500 font-normal">· {formatDateLocal(doc.created_at)}</span>
                              )}
                              {status && (
                                <span className="ml-2">
                                  <span
                                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                      isReady ? 'bg-green-100 text-green-800' : isProcessing ? 'bg-yellow-100 text-yellow-800' : isFailed ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {isReady ? 'Ready' : isProcessing ? 'Processing' : isFailed ? 'Failed' : status}
                                  </span>
                                </span>
                              )}
                            </p>
                            {doc.summary_text && (
                              <p className="text-xs text-gray-500 truncate mt-0.5" title={doc.summary_text}>
                                {(doc.summary_text || '').length > 75 ? `${(doc.summary_text || '').slice(0, 75)}…` : doc.summary_text}
                              </p>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="text-gray-600 font-medium">Data source not found</p>
              <button
                type="button"
                onClick={() => navigate('/data-sources')}
                className="mt-3 text-primary-600 hover:text-primary-700 text-sm"
              >
                Back to Data Sources
              </button>
            </div>
          )}
        </div>
      </div>

        {/* Delete Data Source Confirmation Modal - same style as folder delete (mandatory checkbox) */}
        {dataSourceToDelete && (
          <Modal
            isOpen
            onClose={handleCloseDeleteModal}
            title="Delete Data Source"
            maxWidth="md"
          >
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete &quot;{dataSourceToDelete.display_name}&quot;?
                  </h3>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-gray-600">
                      This action cannot be undone.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                      <p className="text-sm text-yellow-800 font-medium">
                        ⚠️ Warning: This data source and its folder will be deleted
                      </p>
                      <p className="text-sm text-yellow-700 mt-1">
                        The data source, its folder, and all documents synced from it will be deleted. Documents will no longer appear in your knowledge base.
                      </p>
                      <div className="mt-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={deleteDataSourceConfirmed}
                            onChange={(e) => setDeleteDataSourceConfirmed(e.target.checked)}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                          />
                          <span className="text-sm text-yellow-800 cursor-pointer">
                            I understand and want to delete this data source and its folder and all synced documents
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDeleteModal}
                  disabled={!!deletingId}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDeleteConfirm}
                  loading={!!deletingId}
                  disabled={!!deletingId || !deleteDataSourceConfirmed}
                >
                  Delete Data Source
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Data Sources Info Modal */}
        {showInfoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <Database className="h-6 w-6 text-primary-600" />
                    </div>
                    <h2 className="text-2xl font-semibold text-gray-900">What are Data Sources?</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowInfoModal(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <Info className="h-5 w-5 mr-2 text-primary-600" />
                      Definition
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Data Sources</strong> are connected external services—like <strong>Google Drive</strong> or <strong>Google Search Console</strong>—that sync content into your workspace. Each connection becomes a folder in your knowledge base, and synced documents are used when you chat in folders that include that context.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">How it works</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                      <li>Connect a service (e.g. Google Drive) and choose what to sync (e.g. a folder).</li>
                      <li>Documents are synced into a workspace folder and indexed for search.</li>
                      <li>Add that folder or data source as <strong>context</strong> to chat folders so conversations can use the synced content.</li>
                      <li>Re-sync anytime to pull in new or updated files.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Sources vs uploads</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm text-gray-700">
                      <p>
                        <strong>Data Sources</strong> keep a live link to an external service: you sync on a schedule or on demand, and new or updated files can be pulled in without uploading again. Use them for Google Drive folders, Search Console properties, and similar connectors.
                      </p>
                      <p>
                        <strong>Uploaded documents</strong> (Knowledge Base or Global Documents) are files you upload once; they don’t sync from an external service.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Supported sources</h3>
                    <p className="text-gray-700 text-sm mb-2">More connectors may be added over time. Currently:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm ml-2">
                      <li><strong>Google Drive</strong> — sync files (PDF, DOCX, XLSX, etc.) from a Drive folder.</li>
                      <li><strong>Google Search Console</strong> — sync search performance data for your properties.</li>
                    </ul>
                  </div>

                  <div className="bg-primary-50 border-l-4 border-primary-400 p-4">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-primary-900 mb-1">Tip</p>
                        <p className="text-sm text-primary-800">
                          After adding a data source, add it as context to the folders where you want its documents to be used. Without that step, synced content won’t be included in chat.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setShowInfoModal(false)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default DataSources;
